import { getDecimalSeparator, getStoredRegionLocale } from "utils/regionUtils";

/**
 * A collection of BOS data type functions.
 * This could be used to transform units
 */
const DataType = {
	/**
	 * Transform value from big decimal to normal decimal
	 *
	 * @param {number} value
	 * @return {number} value divided by 100.000
	 */
	fromBigDecimal: (value, replaceDot) => {
		if (DataType.isValidNumber(value)) {
			const format = value / 100000;
			if (replaceDot) {
				return format.toString().replace(".", getDecimalSeparator(getStoredRegionLocale()));
			}
			return format;
		} else {
			return undefined;
		}
	},

	/**
	 * Transform value from normal decimal to big decimal
	 *
	 * @param {number} value
	 * @return {number} value multiplied by 100.000
	 */
	toBigDecimal: value => DataType.isValidNumber(value) ? value * 100000 : undefined,

	/**
	 * Checks if the given value is a valid string
	 *
	 * @example
	 * DataType.isValidString(null); // false
	 * DataType.isValidString(undefined); // false
	 * DataType.isValidString(""); // false
	 * DataType.isValidString(5); // false
	 * DataType.isValidString("abc"); // true
	 * DataType.isValidString("123"); // true
	 *
	 * @param {string} value - value to be checked
	 * @return {boolean} whether the value is valid or not
	 */
	isValidString: value => !!value && typeof value === "string",

	/**
	 * Checks if the given value is a valid number
	 *
	 * @example
	 * isValidNumber(null); // false
	 * isValidNumber(undefined); // false
	 * isValidNumber(""); // false
	 * isValidNumber(5); // true
	 * isValidNumber("abc"); // false
	 * isValidNumber("123"); // true
	 *
	 * @param {string|number} value - value to be checked
	 * @return {boolean} whether the value is valid or not
	 */
	isValidNumber: value => (value === 0 || !!value)
		&& (typeof value === "number" || !!parseInt(value) || parseInt(value) === 0),

	/**
	 * @param {number} value - price of unit in bigdecimal format
	 * @param {number} quantity - amount of unit in bigdecimal format
	 */
	priceWithQuantity: (value, quantity) => {
		const convertedValue = DataType.fromBigDecimal(value);
		const convertedQuantity = DataType.fromBigDecimal(quantity);
		return convertedValue && convertedQuantity ? convertedValue * convertedQuantity : undefined;
	},

	/**
	 * @param {number} value - value to be multiplied by vat
	 * @param {number} vat - vat percentage, not in bigdecimal format!
	 */
	priceMultipliedByVat: (value, vat) => value + (value * (vat / 100)),

	/**
	 * Parses boolean or string values as boolean
	 *
	 * @example
	 * DataType.asBoolean(null); // undefined
	 * DataType.asBoolean(undefined); // undefined
	 * DataType.asBoolean(""); // undefined
	 * DataType.asBoolean(true); // true
	 * DataType.asBoolean("false"); // false
	 * DataType.asBoolean("True"); // true
	 * DataType.asBoolean("1"); // undefined
	 *
	 * @param {string|boolean} value - value to be parsed
	 * @return {boolean|undefined} parsed boolean or if not able to parse undefined
	 */
	asBoolean: value => {
		if (typeof value === "boolean") {
			return value;
		} else if (DataType.isValidString(value)
			&& (value.toLowerCase() === "true" || value.toLowerCase() === "false")) {
			return value === "true";
		} else {
			return undefined;
		}
	},

	/**
	 * Checks if the given value is a valid boolean.
	 * This will use {@link DataType.asBoolean} if the return values is not undefined
	 *
	 * @param {string} value - value to be checked
	 * @return {boolean} whether the value is valid or not
	 */
	isValidBoolean: value => DataType.asBoolean(value) !== undefined,

	/**
	 * Round the given value to the given decimals.
	 * This will use {@link DataType.isValidNumber} if the given value is not valid
	 *
	 * @param {number|string} value - value to be rounded
	 * @param {number|string} decimal - decimals to be rounded
	 * @return {number} rounded number
	 */
	roundDecimal: (value, decimals = 1) => DataType.isValidNumber(value) ? Number(parseFloat(value).toFixed(decimals)) : undefined,
};

export default DataType;
